import type { AccountInfo, PublicClientApplication } from "@azure/msal-browser";
import { dispatchApiRequest, loginRequest } from "@/config/msal-config";
import { notifyBugsnag } from "@/lib/bugsnag";
import {
    clearMsalCacheArtifacts,
    hasStoredMsalSessionHint,
    getStoredMsalSessionHint,
    setStoredMsalSessionHint,
} from "@/services/msalSessionState";
import { getStoredInternalUserProfile } from "@/services/internalSessionProfileStorage";
import {
    getTokenData,
    setTokenData,
    type TokenData,
} from "@/services/tokenStorage";
import { buildAuthDeviceInfo, getOrCreateDeviceId } from "@/services/deviceId";
import { applySessionAccountInactiveClientState } from "@/services/accountAccessState";
import {
    getStoredExternalUserProfile,
    hasStoredExternalUserProfile,
    setStoredExternalUserProfile,
} from "@/services/webSessionStorage";
import logger from "@/utils/logger";

const TOKEN_REFRESH_SKEW_MS = 60_000;

let pendingRenewal: Promise<TokenData> | null = null;

type RefreshReason = "expired" | "unauthorized";

export class DispatchAuthError extends Error {
    code: string;
    status?: number;

    constructor(message: string, code: string, status?: number) {
        super(message);
        this.name = "DispatchAuthError";
        this.code = code;
        this.status = status;

        Object.setPrototypeOf(this, DispatchAuthError.prototype);
    }
}

export const isDispatchAuthError = (
    error: unknown
): error is DispatchAuthError => error instanceof DispatchAuthError;

type WindowWithMsal = Window & {
    __msalInstance?: PublicClientApplication;
};

const getApiBaseUrl = () =>
    process.env.NEXT_PUBLIC_API_URL ||
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    "";

const getWindowMsalInstance = (): PublicClientApplication | null => {
    if (typeof window === "undefined") {
        return null;
    }

    return (window as WindowWithMsal).__msalInstance ?? null;
};

const getDeviceId = (): string | null => {
    if (typeof window === "undefined") {
        return null;
    }

    return getOrCreateDeviceId();
};

const isExternalSession = (): boolean => {
    return hasStoredExternalUserProfile();
};

const hasInternalSessionHints = (): boolean => {
    if (typeof window === "undefined" || isExternalSession()) {
        return false;
    }

    if (Boolean(getStoredInternalUserProfile())) {
        return true;
    }

    if (hasStoredMsalSessionHint()) {
        return true;
    }

    const msalInstance = getWindowMsalInstance();
    if (!msalInstance) {
        return false;
    }

    return Boolean(
        msalInstance.getActiveAccount() || msalInstance.getAllAccounts().length
    );
};

const parseJsonSafe = (raw: string): unknown => {
    try {
        return JSON.parse(raw);
    } catch {
        return raw;
    }
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
    value && typeof value === "object"
        ? (value as Record<string, unknown>)
        : null;

const readString = (
    record: Record<string, unknown> | null,
    key: string
): string | undefined => {
    if (!record) {
        return undefined;
    }

    const value = record[key];
    return typeof value === "string" && value.trim() ? value : undefined;
};

const readNumber = (
    record: Record<string, unknown> | null,
    key: string
): number | undefined => {
    if (!record) {
        return undefined;
    }

    const value = record[key];
    return typeof value === "number" && Number.isFinite(value)
        ? value
        : undefined;
};

const extractErrorCode = (payload: Record<string, unknown> | null) => {
    const errorCode =
        readString(payload, "errorCode") || readString(payload, "ErrorCode");
    return errorCode?.trim() || undefined;
};

const extractErrorMessage = (
    payload: Record<string, unknown> | null,
    fallbackMessage: string
) =>
    readString(payload, "message") ||
    readString(payload, "Message") ||
    readString(payload, "error") ||
    fallbackMessage;

const isAccountInactivePayload = (
    payload: Record<string, unknown> | null,
    rawText: string
) => {
    const errorCode = extractErrorCode(payload);
    if (errorCode === "ACCOUNT_INACTIVE") {
        return true;
    }

    return /account (is )?inactive/i.test(rawText);
};

const throwAccountInactiveError = (
    payload: Record<string, unknown> | null,
    rawText: string,
    status: number
): never => {
    const message = extractErrorMessage(
        payload,
        "Your account is inactive. Please contact your administrator."
    );
    applySessionAccountInactiveClientState({
        message,
        redirectPath: isExternalSession() ? "/externalLogin" : "/",
    });
    throw new DispatchAuthError(message, "ACCOUNT_INACTIVE", status);
};

const extractTokenData = (
    payload: unknown,
    previousRefreshToken: string
): TokenData | null => {
    const root = asRecord(payload);
    const nestedUserProfile = asRecord(root?.userProfile);

    const accessToken =
        readString(root, "accessToken") ||
        readString(nestedUserProfile, "accessToken") ||
        readString(root, "bearerToken");

    const accessTokenExpiresAt =
        readString(root, "accessTokenExpiresAt") ||
        readString(nestedUserProfile, "accessTokenExpiresAt");

    let expiresAt: number | null = null;
    if (accessTokenExpiresAt) {
        const parsed = new Date(accessTokenExpiresAt).getTime();
        expiresAt = Number.isFinite(parsed) ? parsed : null;
    } else {
        const expiresIn =
            readNumber(root, "expiresIn") || readNumber(root, "ExpiresIn");
        if (expiresIn && expiresIn > 0) {
            expiresAt = Date.now() + Math.max(expiresIn - 30, 1) * 1000;
        }
    }

    if (!accessToken || !expiresAt) {
        return null;
    }

    const refreshToken =
        readString(root, "refreshToken") ||
        readString(nestedUserProfile, "refreshToken") ||
        previousRefreshToken;

    return {
        accessToken,
        refreshToken,
        expiresAt,
    };
};

const syncExternalProfileFromRefresh = (payload: unknown): void => {
    if (typeof window === "undefined" || !isExternalSession()) {
        return;
    }

    const currentProfile =
        getStoredExternalUserProfile<Record<string, unknown>>();
    if (!currentProfile) {
        return;
    }

    try {
        const root = asRecord(payload);
        const nestedUserProfile = asRecord(root?.userProfile);

        const mergedProfile = {
            ...currentProfile,
            ...(nestedUserProfile ?? {}),
        };

        setStoredExternalUserProfile(mergedProfile);
    } catch (error) {
        logger.warn("Failed to sync refreshed external profile", error);
    }
};

const buildSyntheticMsalProfile = (account: AccountInfo) => ({
    id: account.localAccountId || account.homeAccountId || account.username,
    mail: account.username,
    userPrincipalName: account.username,
    displayName: account.name || account.username,
    givenName: "",
    surname: "",
    businessPhones: [],
    homePhones: [],
});

const getMsalAccount = (
    msalInstance: PublicClientApplication
): AccountInfo | null => {
    const activeAccount = msalInstance.getActiveAccount();
    if (activeAccount) {
        return activeAccount;
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
        return null;
    }

    msalInstance.setActiveAccount(accounts[0]);
    return accounts[0];
};

const renewWithRefreshToken = async (
    existingToken: TokenData | null
): Promise<TokenData | null> => {
    const shouldAttemptCookieBackedRenewal =
        isExternalSession() || hasInternalSessionHints();
    if (!existingToken?.refreshToken && !shouldAttemptCookieBackedRenewal) {
        return null;
    }

    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) {
        return null;
    }

    const url = `${apiBaseUrl.replace(/\/$/, "")}/api/auth/refresh-session`;
    const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            refreshToken: existingToken?.refreshToken ?? "",
            deviceId: getDeviceId(),
        }),
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        const errorPayload =
            errorText.trim().length > 0
                ? asRecord(parseJsonSafe(errorText))
                : null;

        if (isAccountInactivePayload(errorPayload, errorText)) {
            throwAccountInactiveError(errorPayload, errorText, response.status);
        }

        throw new Error(
            `Dispatch refresh-session failed (${response.status} ${response.statusText})${errorText ? `: ${errorText}` : ""}`
        );
    }

    const responseText = await response.text().catch(() => "");
    const payload =
        responseText.trim().length > 0 ? parseJsonSafe(responseText) : {};
    const previousRefreshToken = existingToken?.refreshToken ?? "";
    const tokenData = extractTokenData(payload, previousRefreshToken);

    if (!tokenData) {
        logger.info(
            "Dispatch refresh-session response did not include a usable access token"
        );
        return null;
    }

    setTokenData({
        ...tokenData,
        refreshToken: "",
    });
    syncExternalProfileFromRefresh(payload);
    return {
        ...tokenData,
        refreshToken: "",
    };
};

const renewWithMsal = async (
    existingToken: TokenData | null
): Promise<TokenData | null> => {
    if (typeof window === "undefined" || isExternalSession()) {
        return null;
    }

    const msalInstance = getWindowMsalInstance();
    if (!msalInstance) {
        return null;
    }

    let account = getMsalAccount(msalInstance);

    if (!account) {
        const storedMsalHint = getStoredMsalSessionHint();
        if (!storedMsalHint?.username) {
            return null;
        }

        const hintRestore = await msalInstance.ssoSilent({
            ...loginRequest,
            loginHint: storedMsalHint.username,
        });

        if (!hintRestore.account) {
            return null;
        }

        account = hintRestore.account;
        msalInstance.setActiveAccount(account);
        setStoredMsalSessionHint(account);
    }

    const aadToken = await msalInstance.acquireTokenSilent({
        ...dispatchApiRequest,
        account,
    });

    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) {
        return null;
    }

    const graphProfile = buildSyntheticMsalProfile(account);
    const response = await fetch(
        `${apiBaseUrl.replace(/\/$/, "")}/api/user/msal-login-authz`,
        {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-AAD-Authorization": `Bearer ${aadToken.accessToken}`,
            },
            body: JSON.stringify({
                email:
                    graphProfile.mail || graphProfile.userPrincipalName || "",
                displayName:
                    graphProfile.displayName ||
                    graphProfile.mail ||
                    graphProfile.userPrincipalName ||
                    "Unknown User",
                deviceInfo: buildAuthDeviceInfo(),
            }),
        }
    );

    if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        const errorPayload =
            errorText.trim().length > 0
                ? asRecord(parseJsonSafe(errorText))
                : null;

        if (isAccountInactivePayload(errorPayload, errorText)) {
            throwAccountInactiveError(errorPayload, errorText, response.status);
        }

        throw new Error(
            `MSAL login failed (${response.status} ${response.statusText})${errorText ? `: ${errorText}` : ""}`
        );
    }

    const responseText = await response.text().catch(() => "");
    const userProfile =
        responseText.trim().length > 0 ? parseJsonSafe(responseText) : {};

    const tokenData = extractTokenData(
        userProfile,
        existingToken?.refreshToken ?? ""
    );
    if (!tokenData) {
        throw new Error("MSAL silent Dispatch renewal did not return a token");
    }

    const sanitizedTokenData = {
        ...tokenData,
        refreshToken: "",
    };

    clearMsalCacheArtifacts(window.localStorage);
    setTokenData(sanitizedTokenData);
    return sanitizedTokenData;
};

const renewDispatchToken = async (
    reason: RefreshReason
): Promise<TokenData> => {
    if (pendingRenewal) {
        return pendingRenewal;
    }

    pendingRenewal = (async () => {
        const existingToken = getTokenData();
        const tokenStillFresh =
            existingToken &&
            existingToken.expiresAt - Date.now() > TOKEN_REFRESH_SKEW_MS;

        if (tokenStillFresh && reason !== "unauthorized") {
            return existingToken;
        }

        let lastError: unknown = null;

        try {
            const refreshedWithRefreshToken =
                await renewWithRefreshToken(existingToken);
            if (refreshedWithRefreshToken) {
                return refreshedWithRefreshToken;
            }
        } catch (error) {
            if (isDispatchAuthError(error)) {
                throw error;
            }
            lastError = error;
            logger.warn("Dispatch refresh token renewal failed", error);
        }

        try {
            const refreshedWithMsal = await renewWithMsal(existingToken);
            if (refreshedWithMsal) {
                return refreshedWithMsal;
            }
        } catch (error) {
            if (isDispatchAuthError(error)) {
                throw error;
            }
            lastError = error;
            logger.warn("Dispatch MSAL renewal failed", error);
        }

        notifyBugsnag(lastError ?? new Error("dispatch-token-renewal-failed"), {
            kind: "dispatch-token-renewal-failed",
            reason,
            hasRefreshToken: Boolean(existingToken?.refreshToken),
            hasMsalInstance: Boolean(getWindowMsalInstance()),
            loginType: isExternalSession() ? "external" : "msal-or-unknown",
        });

        throw new Error("AUTH_EXPIRED");
    })();

    try {
        return await pendingRenewal;
    } finally {
        pendingRenewal = null;
    }
};

export async function ensureValidDispatchToken(): Promise<string> {
    const existingToken = getTokenData();

    if (
        existingToken &&
        existingToken.expiresAt - Date.now() > TOKEN_REFRESH_SKEW_MS
    ) {
        return existingToken.accessToken;
    }

    const refreshedToken = await renewDispatchToken("expired");
    return refreshedToken.accessToken;
}

export async function forceRefreshDispatchToken(): Promise<string> {
    const refreshedToken = await renewDispatchToken("unauthorized");
    return refreshedToken.accessToken;
}
